/**
 * Staff JWT claims.
 *
 * A Supabase `custom_access_token_hook` injects tenancy + permissions into
 * `app_metadata` at token-mint time, so RLS policies can do indexed equality
 * against the claim instead of an `EXISTS` subquery over `accounts_memberships`
 * for every row of every join. That rewrite is the fix for the per-row fetch
 * cost we hit on the MakerKit policies.
 *
 * Keep this in sync with `supabase/schemas/50-auth-hook.sql`.
 */
import type { Session } from '@supabase/supabase-js';

/** Mirrors the `public.app_permissions` enum. */
export type Permission =
  | 'roles.manage'
  | 'billing.manage'
  | 'settings.manage'
  | 'members.manage'
  | 'invites.manage'
  | 'locations.manage'
  | 'catalog.manage'
  | 'inventory.manage'
  | 'customers.manage'
  | 'orders.manage'
  | 'delivery.manage'
  | 'fleet.manage'
  | 'discounts.manage'
  | 'loyalty.manage'
  | 'payments.manage'
  | 'taxes.manage'
  | 'cms.manage'
  | 'marketing.manage'
  | 'compliance.manage'
  | 'reports.view';

export interface StaffClaims {
  /** Every account the user is a member of, the personal one included. */
  accountIds: string[];
  /**
   * The team accounts only.
   *
   * The console is for operating a dispensary, and a personal account holds
   * none — no products, no capabilities — so it is never something to switch
   * to. Only team memberships carry a role, which is what tells them apart.
   */
  teamAccountIds: string[];
  /** Permissions, keyed by account id. */
  permissionsByAccount: Record<string, Permission[]>;
  /** Role slug, keyed by account id. */
  rolesByAccount: Record<string, string>;
  /**
   * Per-member section capabilities, keyed by account id.
   *
   * Distinct from permissions: this is `accounts_memberships.resources`, the
   * list of admin sections a member may open (`catalog`, `orders`, …). The RLS
   * policies added in 20260828020000 enforce it, so the UI hides what it names
   * rather than linking to screens that would come back empty.
   */
  resourcesByAccount: Record<string, string[]>;
  /**
   * From the pre-existing `app_metadata.role` claim, not the custom hook.
   * Note the DB additionally requires AAL2 for super-admin reads
   * (`public.is_super_admin`), so this alone does not grant access.
   */
  isSuperAdmin: boolean;
}

const EMPTY: StaffClaims = {
  accountIds: [],
  teamAccountIds: [],
  permissionsByAccount: {},
  rolesByAccount: {},
  resourcesByAccount: {},
  isSuperAdmin: false,
};

/**
 * Read claims out of a session's access token.
 *
 * Decodes without verifying — safe here because the value is only used to shape
 * the UI. Every actual read is independently enforced by RLS (admin) or the
 * repository layer (API); a tampered token buys nothing.
 */
export function readStaffClaims(session: Session | null): StaffClaims {
  if (!session?.access_token) return EMPTY;

  const payload = decodeJwtPayload(session.access_token);
  if (!payload) return EMPTY;

  const meta = (payload.app_metadata ?? {}) as Record<string, unknown>;

  const accountIds = asStringArray(meta.account_ids);
  const rolesByAccount = (meta.roles ?? {}) as Record<string, string>;

  return {
    accountIds,
    teamAccountIds: accountIds.filter((id) => id in rolesByAccount),
    permissionsByAccount: (meta.permissions ?? {}) as Record<string, Permission[]>,
    rolesByAccount,
    resourcesByAccount: (meta.resources ?? {}) as Record<string, string[]>,
    isSuperAdmin: meta.role === 'super-admin',
  };
}

export function hasPermission(
  claims: StaffClaims,
  accountId: string,
  permission: Permission,
): boolean {
  if (claims.isSuperAdmin) return true;
  return claims.permissionsByAccount[accountId]?.includes(permission) ?? false;
}

/**
 * Does this member hold a section capability?
 *
 * Mirrors `public.jwt_has_resource`: super admins and anyone with
 * `members.manage` pass, matching what `membership_has_resource` decided.
 */
export function hasResource(claims: StaffClaims, accountId: string, resource: string): boolean {
  if (claims.isSuperAdmin) return true;
  if (hasPermission(claims, accountId, 'members.manage')) return true;

  return claims.resourcesByAccount[accountId]?.includes(resource) ?? false;
}

export function isMemberOf(claims: StaffClaims, accountId: string): boolean {
  return claims.isSuperAdmin || claims.accountIds.includes(accountId);
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}
